import { CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpEventType } from '@angular/common/http';
import { AfterViewInit, Component, DoCheck, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FamilyCodes } from 'src/app/core/_enums/family.code';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { CeuValue } from 'src/app/core/_models/ceuValues';
import { DetailedOperation } from 'src/app/core/_models/detailedOperation';
import { Energie } from 'src/app/core/_models/energie';
import { EnginOperation } from 'src/app/core/_models/enginOperation';
import { Indicator } from 'src/app/core/_models/indicator';
import { DredgingProjectUsageResourceModel, Operation, TunnelingProjectUsageResourceModel } from 'src/app/core/_models/operation';
import { assignCreatedEquipmentIds, EquipmentProofKind, PendingEquipmentProof } from '../../project-equipment-proofs.util';
import { ProductInOut } from 'src/app/core/_models/productInOut';
import { ProductOperation } from 'src/app/core/_models/productOperation';
import { Project } from 'src/app/core/_models/project';
import { ProjectType } from 'src/app/core/_models/projectType';
import { Solution } from 'src/app/core/_models/solution';
import { Transport } from 'src/app/core/_models/transport';
import { Vehicle } from 'src/app/core/_models/vehicle';
import { AcheminementService } from 'src/app/core/_services/acheminement.service';
import { AdministrationService } from 'src/app/core/_services/administration.service';
import { EnginAtelierService } from 'src/app/core/_services/engin.atelier.service';
import { OperationService } from 'src/app/core/_services/operation.service';
import { ProductFormulaService } from 'src/app/core/_services/product.formula.service';
import { ProjectAutoSaveService } from 'src/app/core/_services/project-auto-save.service';
import { ProjectService } from 'src/app/core/_services/project.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SolutionService } from 'src/app/core/_services/solution.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TransportService } from 'src/app/core/_services/transport.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { GlobalConstants } from 'src/constants/global.constants';
import { DialogAddCompositionComponent } from '../../../shared/dialog-add-composition/dialog-add-composition.component';
import { DialogCopyOperationComponent } from '../../../shared/dialog-copy-operation/dialog-copy-operation.component';
import { DialogDredgingEquipmentComponent, DialogDredgingEquipmentComponentResponseDataModel } from '../../../shared/dialog-dredging-equipment/dialog-dredging-equipment.component';
import { DialogEditEnginComponent } from '../../../shared/dialog-edit-engin/dialog-edit-engin.component';
import { DialogEditOperationComponent } from '../../../shared/dialog-edit-operation/dialog-edit-operation.component';
import { DialogEditProductMultimodalQuantityComponent } from '../../../shared/dialog-edit-product-multimodal-quantity/dialog-edit-product-multimodal-quantity.component';
import { DialogOverviewComponent } from '../../../shared/dialog-overview/dialog-overview.component';
import { DialogSolutionComponent } from '../../../shared/dialog-solution/dialog-solution.component';
import { DialogTunnelingEquipmentComponent, DialogTunnelingEquipmentComponentResponseDataModel } from '../../../shared/dialog-tunneling-equipment/dialog-tunneling-equipment.component';
import { ProjectDataService } from '../../project-data.service';
import { computeProjectErrors } from '../../project-errors.util';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';

export enum ResourceType {
  Tunnelier = 'tunnelier',
  Dredge = 'dredge'
}

@Component({
  selector: 'app-project-step-solutions',
  templateUrl: './project-step-solutions.component.html',
  styleUrls: ['./project-step-solutions.component.scss']
})
export class ProjectStepSolutionsComponent implements OnInit, OnChanges, DoCheck, AfterViewInit, OnDestroy {
  SiteMapUrl = SiteMapUrl;
  EMPTY_GUID = GlobalConstants.EMPTY_GUID;
  private destroy$ = new Subject<void>();

  @Input() projectForm!: FormGroup;
  @Input() projectId: string | null = null;
  @Input() currentItem!: Project;
  @Input() autoSaveEnabled = true;
  @Input() flushChanges: () => Promise<boolean> = () => Promise.resolve(false);

  get isFromCallForTenders(): boolean {
    return this.currentItem?.isFromCallForTenders === true;
  }

  @Output() onNextStep = new EventEmitter<void>();
  @Output() onPreviousStep = new EventEmitter<void>();
  @Output() onSaveProject = new EventEmitter<{ item: Project; silent: boolean }>();
  @Output() onEquipmentProofPending = new EventEmitter<PendingEquipmentProof>();

  @ViewChild('fileWaterInput', { static: false }) fileWaterInput!: ElementRef;
  @ViewChild('fileBioInput', { static: false }) fileBioInput!: ElementRef;
  @ViewChildren('panelRef') panelRefs!: QueryList<any>;
  @ViewChildren('operationDropList') operationDropLists!: QueryList<CdkDropList>;
  operationDropListsArray: CdkDropList[] = [];
  private dropListDataCache = new Map<string, any>();

  getDropListData(operation: any): any {
    const id = operation?.id;
    if (!id) return {};
    if (!this.dropListDataCache.has(id)) {
      this.dropListDataCache.set(id, { operationId: id });
    }
    return this.dropListDataCache.get(id);
  }

  form!: FormGroup;
  solutionForm!: FormGroup;
  selected = new FormControl(0);
  indicators: Indicator[] = [];
  indexEnergyToShow: number = 0;
  totalCost: number = 0;

  inputProductsList: Transport[] = [];
  private transportsVersionId?: string;
  energies!: Energie[];
  vehicleEnergies!: Energie[];
  allProjectTypes!: ProjectType[];
  filteredProjectTypes: ProjectType[] = [];
  biodiversityList!: any[];
  waterList!: any[];

  displayAsDay: boolean = true;
  hoursForDay?: number;

  private panelStates: Array<{ id: string; expanded: boolean }> = [];
  isLoadingProducts: boolean = false;
  isLoadingInitialData: boolean = false;
  showIncompleteProductsWarning: boolean = false;
  incompleteProductsList: string[] = [];
  private initialDataLoaded: boolean = false;

  constructor(
    private projectService: ProjectService,
    private dialog: MatDialog,
    private spinnerService: SpinnerOverlayService,
    private acheminementService: AcheminementService,
    private snackbarService: SnackbarService,
    private productFormulaService: ProductFormulaService,
    private enginService: EnginAtelierService,
    private operationService: OperationService,
    private solutionService: SolutionService,
    private uploadService: UploadService,
    private administrationService: AdministrationService,
    private formBuilder: FormBuilder,
    private projectDataService: ProjectDataService,
    private translateService: TranslateService,
    private transportService: TransportService,
    private autoSaveService: ProjectAutoSaveService,
    public labelI18n: LabelI18nService
  ) {
    this.administrationService.get().subscribe((data) => (this.hoursForDay = data.dailyUsageTime));
  }

  ngAfterViewInit(): void {
    this.operationDropListsArray = this.operationDropLists.toArray();
    this.operationDropLists.changes.subscribe(() => {
      this.operationDropListsArray = this.operationDropLists.toArray();
    });
  }

  ngOnInit(): void {
    this.form = this.projectForm;
    this.initializeSolutionForm();
    this.solutionForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.saveSolutionFormData();
      if (!this.solutionForm.dirty) return;
      this.markUnsavedIfDeferred();
    });
    this.solutionForm.valueChanges.pipe(debounceTime(800), takeUntil(this.destroy$)).subscribe(() => {
      if (!this.solutionForm.dirty) return;
      this.solutionForm.markAsPristine();
      this.autoSaveProject(this.currentItem, true);
    });

    if (!this.currentItem.solutions) {
      this.currentItem.solutions = [];
    }

    if (!this.initialDataLoaded) {
      this.loadInitialData();
    } else {
      this.isLoadingInitialData = false;
      this.updateSolutionForm();
      this.checkIncompleteProducts();
      this.changeTotalToShow();
    }

    this.selected.setValue(0);
    setTimeout(() => {
      this.updateSolutionForm();
      this.checkIncompleteProducts();
      this.changeTotalToShow();
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentItem'] && this.currentItem) {
      if (!this.currentItem.solutions) {
        this.currentItem.solutions = [];
      }

      if (this.initialDataLoaded) {
        this.isLoadingInitialData = false;
      }

      this.updateFilteredProjectTypes();

      setTimeout(() => {
        this.changeTotalToShow();
        this.checkIncompleteProducts();
        this.updateSolutionForm();
      }, 0);
    }
  }

  // L'enregistrement du projet change la version par Object.assign sur currentItem, que ngOnChanges ne voit pas.
  ngDoCheck(): void {
    if (this.currentItem?.versionId && this.currentItem.versionId !== this.transportsVersionId) {
      this.loadTransportsForVersion();
    }
  }

  private initializeSolutionForm(): void {
    this.solutionForm = this.formBuilder.group({
      callForTendersDisplayName: [''],
      description: [''],
      waterIndicator: [0],
      biodiversityIndicator: [0]
    });
  }

  private updateSolutionForm(): void {
    if (!this.currentItem?.solutions || this.selected.value < 0 || !this.solutionForm) return;

    const currentSolution = this.currentItem.solutions[this.selected.value];
    if (!currentSolution) return;

    this.solutionForm.markAsPristine();
    this.solutionForm.patchValue({
      callForTendersDisplayName: this.currentItem.callForTendersDisplayName || '',
      description: currentSolution.description || '',
      waterIndicator: currentSolution.waterIndicator ?? 0,
      biodiversityIndicator: currentSolution.biodiversityIndicator ?? 0
    });
  }

  private loadTransportsForVersion(): void {
    const versionId = this.currentItem.versionId;
    this.transportsVersionId = versionId;
    this.transportService
      .getAll(undefined, versionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (this.transportsVersionId !== versionId) return;
        this.inputProductsList = data || [];
        this.setTransportName(this.currentItem);
      });
  }

  private loadInitialData(): void {
    this.isLoadingInitialData = true;

    this.projectDataService.loadProjectData().subscribe({
      next: (data) => {
        this.energies = data.energies;
        this.vehicleEnergies = data.vehicleEnergies;
        if ((this.energies?.length > 0 || this.vehicleEnergies?.length > 0) && this.currentItem) {
          this.setVehiclesEnergies(this.currentItem);
          this.setTransportEnergies(this.currentItem);
        }
        this.allProjectTypes = data.projectTypes;
        this.biodiversityList = data.biodiversityList;
        this.waterList = data.waterList;
        this.indicators = data.indicators;

        this.isLoadingInitialData = false;
        this.initialDataLoaded = true;

        this.updateFilteredProjectTypes();

        if (this.indicators && this.indicators.length > 0) {
          const co2IndicatorIndex = this.indicators.findIndex((indicator) => indicator.code === 'CO');
          this.indexEnergyToShow = co2IndicatorIndex >= 0 ? co2IndicatorIndex : 0;
        }

        setTimeout(() => {
          this.updateSolutionForm();
          this.checkIncompleteProducts();
          this.changeTotalToShow();
        }, 0);
      },
      error: () => {
        this.isLoadingInitialData = false;
        this.snackbarService.open(SnackbarTypes.error);
      }
    });
  }

  private enginsMasterById?: Promise<{ [id: string]: any }>;

  private isBlankId(id?: string): boolean {
    return !id || id === GlobalConstants.EMPTY_GUID;
  }

  private sortByRank<T extends { rank?: number }>(lines: T[]): T[] {
    return [...lines].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  }

  private loadEnginsMasterIndex(): Promise<{ [id: string]: any }> {
    if (this.enginsMasterById) return this.enginsMasterById;
    this.enginsMasterById = this.enginService
      .getOnlyEngins(GroupBy.family)
      .toPromise()
      .then((tree) => {
        const map: { [id: string]: any } = {};
        const visit = (node: any) => {
          if (Array.isArray(node)) return node.forEach(visit);
          if (!node || typeof node !== 'object') return;
          if (node.resourceId) map[node.resourceId] = node;
          Object.values(node).forEach(visit);
        };
        visit(tree);
        return map;
      });
    return this.enginsMasterById;
  }

  private async resolveAtelierEnginEnergies(atelierEngins: any[] | undefined): Promise<void> {
    if (!atelierEngins?.length) return;
    const enginsById = await this.loadEnginsMasterIndex();
    const allEnergies = [...(this.vehicleEnergies || []), ...(this.energies || [])];
    atelierEngins.forEach((engin) => {
      const enginId = !this.isBlankId(engin.resourceId) ? engin.resourceId : engin.childResource;
      const canonical = enginsById[enginId];
      if (!canonical?.ruade) engin.energyId = canonical?.energyId;
      engin.energyName = this.isBlankId(engin.energyId) ? undefined : allEnergies.find((e) => e.resourceId === engin.energyId)?.displayName;
    });
  }

  setVehiclesEnergies(currentItem: Project): void {
    const allEnergies = [...(this.vehicleEnergies || []), ...(this.energies || [])];
    for (var sol of currentItem?.solutions ?? []) {
      for (var op of sol?.operations ?? []) {
        for (var veh of op?.detailedOperation?.vehicles ?? []) {
          if (veh.energyId && veh.energyId !== GlobalConstants.EMPTY_GUID) {
            var foundName = allEnergies.find((e) => e.resourceId == veh.energyId)?.displayName;
            if (foundName) {
              veh.energyName = foundName;
            }
          }
          this.resolveAtelierEnginEnergies(veh.atelierEngins);
        }
        for (var tun of op?.detailedOperation?.tunneling ?? []) {
          tun.energyName = this.resolveTunnelerEnergyName(tun.selectedFuel);
        }
      }
    }
  }

  setTransportEnergies(currentItem: Project): void {
    const allEnergies = [...(this.vehicleEnergies || []), ...(this.energies || [])];
    for (var sol of currentItem?.solutions ?? []) {
      for (var op of sol?.operations ?? []) {
        for (var inP of op?.detailedOperation?.inputProducts ?? []) {
          if (inP.energyId && inP.energyId !== GlobalConstants.EMPTY_GUID) {
            var foundName = allEnergies.find((e) => e.resourceId == inP.energyId)?.displayName;
            if (foundName) {
              inP.energyName = foundName;
            }
          }
        }
        for (var outP of op?.detailedOperation?.outputProducts ?? []) {
          if (outP.energyId && outP.energyId !== GlobalConstants.EMPTY_GUID) {
            var foundName = allEnergies.find((e) => e.resourceId == outP.energyId)?.displayName;
            if (foundName) {
              outP.energyName = foundName;
            }
          }
        }
      }
    }
  }

  setTransportName(currentItem: Project): void {
    for (var sol of currentItem?.solutions ?? []) {
      for (var op of sol?.operations ?? []) {
        for (var inP of op?.detailedOperation?.inputProducts ?? []) {
          if (inP.transportId && inP.transportId !== GlobalConstants.EMPTY_GUID) {
            var foundTransportName = this.inputProductsList.find((t) => t.resourceId == inP.transportId)?.displayName;
            if (foundTransportName) {
              inP.transportName = foundTransportName;
            }
          }
        }
        for (var outP of op?.detailedOperation?.outputProducts ?? []) {
          if (outP.transportId && outP.transportId !== GlobalConstants.EMPTY_GUID) {
            var foundTransportName = this.inputProductsList.find((t) => t.resourceId == outP.transportId)?.displayName;
            if (foundTransportName) {
              outP.transportName = foundTransportName;
            }
          }
        }
      }
    }
  }

  private resolveTunnelerEnergyName(selectedFuel?: string): string | undefined {
    if (!selectedFuel || selectedFuel === GlobalConstants.EMPTY_GUID) return undefined;
    return (this.vehicleEnergies || []).find((e) => e.resourceId === selectedFuel)?.displayName;
  }

  private updateFilteredProjectTypes(): void {
    if (!this.allProjectTypes || !this.currentItem?.projectType) return;
    const selectedCodes = this.currentItem.projectType.map((pt) => pt.code);
    this.filteredProjectTypes = this.allProjectTypes.filter((pt) => selectedCodes.includes(pt.code));
  }

  getValue(operation: Operation): number {
    if (!operation.detailedOperation?.ceuValues) return 0;

    if (!this.indicators || !this.indicators[this.indexEnergyToShow]) return 0;

    const total = operation.detailedOperation.ceuValues.find((x: CeuValue) => x.code === this.indicators[this.indexEnergyToShow].code && x.subIndicatorCode === 'TotalCost')?.ceuValue;

    return total ?? 0;
  }

  getProductCeuValue(product: ProductOperation): number {
    if (!product.ceuValues) return 0;
    const indicatorCode = this.indicators[this.indexEnergyToShow]?.code?.toLowerCase();
    if (!indicatorCode) return 0;
    const value = (product.ceuValues as any)[indicatorCode];
    return value ?? 0;
  }

  changeTotalToShow(): void {
    let sum = 0;
    if (this.selected.value >= 0 && this.currentItem.solutions && this.currentItem.solutions.length > 0) {
      const currentSolution = this.currentItem.solutions[this.selected.value];
      if (currentSolution.operations) {
        currentSolution.operations.forEach((operation: Operation) => {
          const value = this.getValue(operation);
          sum += value || 0;
        });
      }
    }
    this.totalCost = sum;
    this.checkIncompleteProducts();
    this.updateSolutionForm();
  }

  getFormattedTotalCost(): string {
    const indicator = this.indicators?.[this.indexEnergyToShow];
    if (!indicator) return '0';
    if (indicator.code === 'CO') {
      return this.totalCost.toLocaleString(this.labelI18n.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return this.labelI18n.exponential(this.totalCost, 2);
  }

  setDetailedOperation(operation: Operation): DetailedOperation {
    const detailedOperation: DetailedOperation = {};
    const productsIn: ProductInOut[] = [];
    const productsOut: ProductInOut[] = [];
    const engins: ProductInOut[] = [];

    operation?.detailedOperation?.inputProducts?.forEach((product) => {
      productsIn.push({
        distance: product.distance,
        doubleFret: product.isDoubleFreight ? 1 : 0,
        energyId: product.energyId,
        quantity: product.quantity,
        resourceId: product.productId,
        transportId: product.transportId,
        workUnit: product.workUnit,
        pipeProduct: product.pipeProduct,
        diameter: product.diameter,
        unitWeight: product.unitWeight,
        productionSite: product.productionSite,
        cableProduct: product.cableProduct,
        section: product.section,
        nbConductors: product.nbConductors,
        operationCompositionId: product.operationCompositionId
      });
    });

    operation?.detailedOperation?.outputProducts?.forEach((product) => {
      productsOut.push({
        distance: product.distance,
        doubleFret: product.isDoubleFreight ? 1 : 0,
        energyId: product.energyId,
        quantity: product.quantity,
        resourceId: product.productId,
        transportId: product.transportId,
        workUnit: product.workUnit,
        pipeProduct: product.pipeProduct,
        diameter: product.diameter,
        unitWeight: product.unitWeight,
        productionSite: product.productionSite,
        cableProduct: product.cableProduct,
        section: product.section,
        nbConductors: product.nbConductors,
        operationCompositionId: product.operationCompositionId
      });
    });

    operation?.detailedOperation?.vehicles?.forEach((vehicle: Vehicle) => {
      engins.push({
        resourceId: vehicle.vehicleId,
        energyId: vehicle.energyId,
        nbDays: vehicle.quantity,
        materialCompositionId: vehicle.materialCompositionId
      } as EnginOperation);
    });

    detailedOperation.engins = engins;
    detailedOperation.productsIn = productsIn;
    detailedOperation.productsOut = productsOut;
    detailedOperation.dredges = operation?.detailedOperation?.dredging;
    detailedOperation.tunnelers = operation?.detailedOperation?.tunneling;

    return detailedOperation;
  }

  updateData(operationId: string, versionId?: string): void {
    if (!this.currentItem?.solutions) return;

    const operation = this.findOperationById(operationId);
    if (!operation) return;

    const detailedOperation = this.setDetailedOperation(operation);
    detailedOperation.versionId = versionId || this.form.value.versionId;

    this.projectService.getProjectCost(detailedOperation).subscribe((response) => {
      if (operation?.detailedOperation) {
        operation.detailedOperation.ceuValues = response;
        setTimeout(() => {
          this.changeTotalToShow();
        }, 0);
      }
    });
    if (!this.autoSaveEnabled) this.refreshLineCeuValues(operation, detailedOperation.versionId);

    this.updateResourceAvailability(operation, detailedOperation);
  }

  private refreshLineCeuValues(operation: Operation, versionId?: string): void {
    const detailed = operation.detailedOperation;
    if (!detailed) return;
    const lines = [
      ...(detailed.inputProducts ?? []).map((line) => ({ line, single: { inputProducts: [line] } })),
      ...(detailed.outputProducts ?? []).map((line) => ({ line, single: { outputProducts: [line] } })),
      ...(detailed.vehicles ?? []).map((line) => ({ line, single: { vehicles: [line] } }))
    ];
    lines.forEach(({ line, single }) => {
      const lineOperation = this.setDetailedOperation({ detailedOperation: single } as Operation);
      lineOperation.versionId = versionId;
      this.projectService.getProjectCost(lineOperation).subscribe((response) => {
        const values: any = { ...line.ceuValues };
        response.filter((value) => value.subIndicatorCode === 'TotalCost' && value.code).forEach((value) => (values[value.code!.toLowerCase()] = value.ceuValue ?? 0));
        line.ceuValues = values;
      });
    });
  }

  private findOperationById(operationId: string): Operation | undefined {
    for (const solution of this.currentItem.solutions || []) {
      const operation = solution.operations?.find((o) => o.id === operationId);
      if (operation) return operation;
    }
    return undefined;
  }

  private updateResourceAvailability(operation: Operation, detailedOperation: DetailedOperation): void {
    this.projectService.CheckResourceForProjectVersion(detailedOperation).subscribe((response) => {
      if (!operation?.detailedOperation) return;

      this.updateProductAvailability(operation, response.detailedOperation?.inputProducts, 'inputProducts');
      this.updateProductAvailability(operation, response.detailedOperation?.outputProducts, 'outputProducts');
      this.updateVehicleAvailability(operation, response.detailedOperation?.vehicles);

      this.checkProjectErrors();
      this.checkIncompleteProducts();
    });
  }

  private updateProductAvailability(operation: Operation, products: ProductOperation[] | undefined, type: 'inputProducts' | 'outputProducts'): void {
    if (!products || !operation?.detailedOperation) return;

    products.forEach((updateProduct: ProductOperation) => {
      if (!updateProduct.operationCompositionId) return;

      const productToUpdate = operation.detailedOperation?.[type]?.find((product) => product.operationCompositionId === updateProduct.operationCompositionId);

      if (productToUpdate) {
        productToUpdate.resourceNotAvailableForThisVersion = updateProduct.resourceNotAvailableForThisVersion;
        productToUpdate.transportationNotAvailableForThisVersion = updateProduct.transportationNotAvailableForThisVersion;
      }
    });
  }

  private updateVehicleAvailability(operation: Operation, vehicles: Vehicle[] | undefined): void {
    if (!vehicles || !operation?.detailedOperation) return;

    vehicles.forEach((updateVehicle: Vehicle) => {
      if (!updateVehicle.materialCompositionId) return;

      const vehicleToUpdate = operation.detailedOperation?.vehicles?.find((vehicle) => vehicle.materialCompositionId === updateVehicle.materialCompositionId);

      if (vehicleToUpdate) {
        vehicleToUpdate.resourceNotAvailableForThisVersion = updateVehicle.resourceNotAvailableForThisVersion;
      }
    });
  }

  checkProjectErrors(): void {
    computeProjectErrors(this.currentItem);
  }

  addSolution(projectId?: string): void {
    if (!projectId) return;

    const title = this.translateService.instant('project_dialog_add_solution');
    const dialogRef = this.dialog.open(DialogSolutionComponent, {
      data: { title }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (!data?.displayName) return;

      this.spinnerService.show();
      if (!this.currentItem.solutions) {
        this.currentItem.solutions = [];
      }

      this.solutionService.add(projectId, { displayName: data.displayName }).subscribe({
        next: (solution: Solution) => {
          if (this.currentItem.solutions && solution) {
            if (this.isFromCallForTenders) {
              solution.callForTendersDisplayName = this.currentItem.callForTendersDisplayName ?? '';
            }
            this.currentItem.solutions.push(solution);
            setTimeout(() => {
              this.selected.setValue(this.currentItem.solutions!.length - 1);
              this.updateSolutionForm();
            });
          }
          this.spinnerService.hide();
        },
        error: () => {
          this.spinnerService.hide();
          this.snackbarService.open(SnackbarTypes.error);
        }
      });
    });
  }

  editSolution(solution: Solution): void {
    const title = this.translateService.instant('project_dialog_edit_solution');
    const dialogRef = this.dialog.open(DialogSolutionComponent, {
      data: { title, displayName: solution?.displayName }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.displayName) {
        solution.displayName = data.displayName;
        this.autoSaveProject(this.currentItem);
      }
    });
  }

  async onDuplicateSolution(resourceId?: string): Promise<void> {
    if (!resourceId || !this.projectId) return;
    if (this.hasDeferredChanges && !(await this.flushChanges())) return;

    this.spinnerService.show();
    this.solutionService.duplicate(resourceId).subscribe({
      next: (res) => {
        if (!res) {
          this.spinnerService.hide();
          return;
        }
        this.projectService.get(this.projectId!).subscribe({
          next: (project) => {
            this.applyDuplicatedProject(project);
            this.snackbarService.open(SnackbarTypes.duplicate);
            this.changeTotalToShow();
            this.spinnerService.hide();
          },
          error: (error) => {
            this.spinnerService.hide();
            this.snackbarService.openServerError(error);
          }
        });
      },
      error: (error) => {
        this.spinnerService.hide();
        this.snackbarService.openServerError(error);
      }
    });
  }

  private applyDuplicatedProject(project: Project): void {
    if (!this.hasDeferredChanges) {
      Object.assign(this.currentItem, project);
      return;
    }

    const solutions = this.currentItem.solutions ?? [];
    const knownIds = new Set(solutions.map((s) => s.id));
    solutions.push(...(project.solutions ?? []).filter((s) => !knownIds.has(s.id)));
    this.currentItem.solutions = solutions;
  }

  onDeleteSolution(indexSolution: number, solutionId?: string): void {
    if (!solutionId) return;

    const title = this.translateService.instant('project_dialog_confirm_delete_solution');
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title, cancelLabel: 'common_btn_cancel', confirmLabel: 'dialog_overview_btn_yes_delete' }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && this.currentItem?.solutions) {
        this.spinnerService.show();
        this.solutionService.delete(solutionId).subscribe({
          next: () => {
            if (this.currentItem.solutions) {
              this.currentItem.solutions.splice(indexSolution, 1);
              this.selected.setValue(this.selected.value - 1);
            }
            this.spinnerService.hide();
          },
          error: () => {
            this.spinnerService.hide();
            this.snackbarService.open(SnackbarTypes.error);
          }
        });
      }
    });
  }

  CheckResourceForProjectVersion(solution: Solution): void {
    solution?.operations?.forEach((operation: Operation) => {
      const detailedOperation = this.setDetailedOperation(operation);
      detailedOperation.versionId = this.form.value.versionId;
      this.updateResourceAvailability(operation, detailedOperation);
    });
  }

  uploadBiodervisityFile(currentItem: Solution, files?: any, biodiversitytype?: string): void {
    if (!currentItem.id || !files || files.length === 0) return;

    const fileToUpload = <File>files[0];
    const formData = new FormData();
    formData.append('file', fileToUpload, `${currentItem.id}_${biodiversitytype}.pdf`);

    this.spinnerService.show();
    this.uploadService.uploadProof(currentItem.id, formData).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.spinnerService.hide();
          if (biodiversitytype === 'w') {
            currentItem.waterFileName = fileToUpload?.name;
            currentItem.waterFileDescription = currentItem.id + '_w.pdf';
          } else {
            currentItem.bioFileName = fileToUpload?.name;
            currentItem.bioFileDescription = currentItem.id + '_b.pdf';
          }
          this.autoSaveProject(this.currentItem);
        }
      },
      error: () => {
        this.spinnerService.hide();
      }
    });
  }

  deleteFileBiodiversity(currentItem: Solution, bioType?: string): void {
    if (bioType === 'w') {
      currentItem.waterFileName = undefined;
    } else {
      currentItem.bioFileName = undefined;
    }

    this.autoSaveProject(this.currentItem);
  }

  addOperation(solution: Solution): void {
    const title = this.translateService.instant('project_dialog_operation_title');
    const dialogRef = this.dialog.open(DialogSolutionComponent, {
      data: { title }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (!data?.displayName || !solution?.id) return;

      this.spinnerService.show();
      if (!solution?.operations) {
        solution.operations = [];
      }

      this.operationService
        .add(solution.id, {
          displayName: data.displayName,
          rank: solution.operations.length + 1,
          type: this.currentItem?.projectType?.length === 1 ? this.currentItem.projectType[0].code : undefined
        })
        .subscribe({
          next: (operation: Operation) => {
            if (solution.operations && operation) {
              solution.operations.push(operation);
              this.changeTotalToShow();
              this.checkIncompleteProducts();
              this.markUnsavedIfDeferred();
            }
            this.spinnerService.hide();
          },
          error: () => {
            this.spinnerService.hide();
            this.snackbarService.open(SnackbarTypes.error);
          }
        });
    });
  }

  editOperation(operation: Operation): void {
    const title = this.translateService.instant('project_dialog_edit_operation');
    const dialogRef = this.dialog.open(DialogSolutionComponent, {
      data: { title, displayName: operation?.displayName }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.displayName) {
        operation.displayName = data.displayName;
        this.autoSaveProject(this.currentItem);
      }
    });
  }

  async onDuplicateOperation(resourceId?: string): Promise<void> {
    if (!resourceId) return;
    if (this.hasDeferredChanges && !(await this.flushChanges())) return;

    this.spinnerService.show();
    this.operationService.duplicate(resourceId).subscribe({
      next: (res) => {
        if (res) {
          const currentSolution = this.currentItem.solutions?.find((s) => s.operations?.some((o) => o.id === resourceId));
          currentSolution?.operations?.push(res);

          if (res.id) {
            this.updateData(res.id.toString());
          }

          this.snackbarService.open(SnackbarTypes.duplicate);
          this.changeTotalToShow();
          this.autoSaveProject(this.currentItem, true);
        }
        this.spinnerService.hide();
      },
      error: (error) => {
        this.spinnerService.hide();
        this.snackbarService.openServerError(error);
      }
    });
  }

  onDeleteOperation(solution: Solution, indexOperation: number, operationId?: string): void {
    if (!operationId) return;

    const title = this.translateService.instant('project_dialog_confirm_delete_operation');
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title, cancelLabel: 'common_btn_cancel', confirmLabel: 'dialog_overview_btn_yes_delete' }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.spinnerService.show();
        this.operationService.delete(operationId).subscribe({
          next: () => {
            if (solution?.operations) {
              solution.operations.splice(indexOperation, 1);
              solution.operations.forEach((op) => {
                if (op.rank && op.rank > indexOperation + 1) {
                  op.rank = op.rank - 1;
                }
              });
              if (!this.shouldDeferPersist()) {
                this.operationService.editRank(solution.operations).catch(() => this.snackbarService.open(SnackbarTypes.error));
              }
              this.changeTotalToShow();
              this.checkIncompleteProducts();
            }
            this.spinnerService.hide();
          },
          error: () => {
            this.spinnerService.hide();
            this.snackbarService.open(SnackbarTypes.error);
          }
        });
      }
    });
  }

  async editRank(solution: Solution, index: number, new_index: number): Promise<void> {
    const operations = solution?.operations;
    if (!operations) return;

    operations.splice(new_index, 0, operations.splice(index, 1)[0]);
    const rank = operations[new_index].rank;
    operations[new_index].rank = operations[index].rank;
    operations[index].rank = rank;

    if (this.shouldDeferPersist()) return;

    this.spinnerService.show();
    try {
      const res = await this.operationService.editRank(operations);
      this.spinnerService.hide();

      if (!res) {
        this.snackbarService.open(SnackbarTypes.error);
      }
    } catch (error) {
      this.spinnerService.hide();
      this.snackbarService.open(SnackbarTypes.error);
    }
  }

  async onOperationDrop(event: CdkDragDrop<Operation[]>, solution: Solution): Promise<void> {
    const operations = solution?.operations;
    if (!operations || event.previousIndex === event.currentIndex) return;

    moveItemInArray(operations, event.previousIndex, event.currentIndex);

    operations.forEach((op, index) => {
      op.rank = index + 1;
    });

    if (this.shouldDeferPersist()) return;

    this.spinnerService.show();
    try {
      const res = await this.operationService.editRank(operations);
      this.spinnerService.hide();

      if (!res) {
        this.snackbarService.open(SnackbarTypes.error);
      }
    } catch (error) {
      this.spinnerService.hide();
      this.snackbarService.open(SnackbarTypes.error);
    }
  }

  onProductDrop(event: CdkDragDrop<any>): void {
    const dragData = event.item.data;
    if (!dragData) return;

    const sourceType: string = dragData.type;
    const sourceOperationId: string = dragData.operationId;
    const targetData = event.container.data;
    const targetOperationId: string = targetData.operationId;

    if (!sourceOperationId || !targetOperationId) return;
    if (sourceOperationId === targetOperationId) return;

    if (sourceType === 'vehicle') {
      this.transferVehicle(dragData.item, sourceOperationId, targetOperationId);
    } else {
      this.transferProduct(dragData.item, sourceType, sourceOperationId, targetOperationId);
    }
  }

  private transferProduct(product: ProductOperation, type: string, sourceOpId: string, targetOpId: string): void {
    if (!product?.operationCompositionId) return;

    this.spinnerService.show();

    const deleteObs$ = type === 'input' ? this.operationService.deleteInboundProduct(sourceOpId, product.operationCompositionId) : this.operationService.deleteOutboundProduct(sourceOpId, product.operationCompositionId);

    const addObs$ = type === 'input' ? this.operationService.addInboundProduct(targetOpId, product) : this.operationService.addOutboundProduct(targetOpId, product);

    const sourceList = this.getProductList(sourceOpId, type);
    const targetList = this.getProductList(targetOpId, type);

    deleteObs$.subscribe({
      next: () => {
        const idx = sourceList.findIndex((p: ProductOperation) => p.operationCompositionId === product.operationCompositionId);
        if (idx > -1) sourceList.splice(idx, 1);
        this.updateData(sourceOpId);

        addObs$.subscribe({
          next: (added: ProductOperation) => {
            targetList.push(added);
            this.updateData(targetOpId);
            this.markUnsavedIfDeferred();
            this.spinnerService.hide();
            this.snackbarService.open(SnackbarTypes.update);
          },
          error: () => this.handleTransferError()
        });
      },
      error: () => this.handleTransferError()
    });
  }

  private transferVehicle(vehicle: Vehicle, sourceOpId: string, targetOpId: string): void {
    if (!vehicle?.materialCompositionId) return;

    this.spinnerService.show();

    this.operationService.deleteVehicle(sourceOpId, vehicle.materialCompositionId).subscribe({
      next: () => {
        const sourceVehicles = this.findOperationById(sourceOpId)?.detailedOperation?.vehicles || [];
        const idx = sourceVehicles.findIndex((v: Vehicle) => v.materialCompositionId === vehicle.materialCompositionId);
        if (idx > -1) sourceVehicles.splice(idx, 1);
        this.updateData(sourceOpId);

        this.operationService.addVehicle(targetOpId, vehicle).subscribe({
          next: (added: Vehicle) => {
            const targetVehicles = this.findOperationById(targetOpId)?.detailedOperation?.vehicles || [];
            targetVehicles.push(added);
            this.updateData(targetOpId);
            this.markUnsavedIfDeferred();
            this.spinnerService.hide();
            this.snackbarService.open(SnackbarTypes.update);
          },
          error: () => this.handleTransferError()
        });
      },
      error: () => this.handleTransferError()
    });
  }

  private getProductList(operationId: string, type: string): ProductOperation[] {
    const op = this.findOperationById(operationId);
    return type === 'input' ? op?.detailedOperation?.inputProducts || [] : op?.detailedOperation?.outputProducts || [];
  }

  private handleTransferError(): void {
    this.snackbarService.open(SnackbarTypes.error);
    this.spinnerService.hide();
  }

  copyOperation(operation: Operation, projectId?: string): void {
    if (!operation || !projectId) return;

    this.spinnerService.show();
    this.projectService.getProjectsToCopyOperation(projectId).subscribe({
      next: (data) => {
        this.spinnerService.hide();
        const dialogRef = this.dialog.open(DialogCopyOperationComponent, {
          data
        });

        dialogRef.afterClosed().subscribe((dataResult) => {
          if (dataResult?.solutionId) {
            this.operationService.copyOperation(operation, dataResult.solutionId).subscribe({
              next: () => {
                this.snackbarService.open(SnackbarTypes.publish);
              },
              error: (error) => {
                this.snackbarService.openServerError(error);
              }
            });
          }
        });
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    });
  }

  addMultiModalTransport(operationId: string, item: ProductOperation): void {
    this.spinnerService.show();
    forkJoin({
      data: this.acheminementService.getAll(),
      favoriteData: this.acheminementService.getAll(GroupBy.favoriteDialog)
    }).subscribe({
      next: ({ data, favoriteData }) => {
        this.spinnerService.hide();
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            title: 'dialog_acheminement_title',
            fullTree: data,
            favoriteData: favoriteData
          }
        });

        dialogRef.afterClosed().subscribe((dataResult) => {
          if (dataResult?.[0]) {
            const fst = dataResult[0];
            item.transportId = fst?.resourceId;
            item.transportName = fst?.displayName;
            item.isMultimodal = true;
            item.transportIsDeleted = false;
            item.transportationNotAvailableForThisVersion = false;
            this.checkProjectErrors();
            this.updateData(operationId);
            this.autoSaveProject(this.currentItem);
          }
        });
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    });
  }

  deleteMultimodalTransport(operationId: string, item: ProductOperation): void {
    if (!item) return;

    item.transportId = undefined;
    item.transportName = undefined;
    item.isMultimodal = false;
    item.transportIsDeleted = false;
    item.transportationNotAvailableForThisVersion = false;
    this.checkProjectErrors();
    this.updateData(operationId);
    this.autoSaveProject(this.currentItem);
  }

  changeFret(operationId: string, item: ProductOperation): void {
    item.isDoubleFreight = !item?.isDoubleFreight;
    this.updateData(operationId);
    this.autoSaveProject(this.currentItem);
  }

  updateItemData(data: any, item: ProductOperation, operationId: string): void {
    const skipTransportAndEnergy = item.isMultimodal && data.transportId === item.transportId;
    const transportChanged = data.transportId !== item.transportId;

    if (skipTransportAndEnergy) {
      const { transportId, transportName, energyId, energyName, energieDescription, isMultimodal, ...restData } = data;
      Object.assign(item, {
        ...restData,
        energyIsDeleted: false,
        transportIsDeleted: false
      });
    } else {
      Object.assign(item, {
        ...data,
        energyIsDeleted: false,
        transportIsDeleted: false
      });
    }

    if (!skipTransportAndEnergy && data.transportId) {
      item.isMultimodal = false;
      if (transportChanged) item.transportationNotAvailableForThisVersion = false;
      const foundTransport = this.inputProductsList.find((t) => t.resourceId === data.transportId);

      if (foundTransport) {
        item.transportId = foundTransport.resourceId;
        item.transportName = foundTransport.displayName;
        item.ruade = foundTransport.ruade;

        if (foundTransport.familyCode !== FamilyCodes.RoadTransportCode) {
          item.energieDescription = foundTransport.energieDescription;
        } else {
          item.energieDescription = undefined;
          if (!item.energyId) {
            const dieselEnergy = this.energies.find((e) => e.code === FamilyCodes.DefaultDieselEnergy) ?? this.energies.find((e) => e.code === FamilyCodes.DefaultDieselEnergyIntl);
            if (dieselEnergy) {
              item.energyId = dieselEnergy.resourceId;
              item.energyName = dieselEnergy.displayName;
            }
          }
        }
      } else {
        item.transportId = undefined;
        item.transportName = undefined;
        item.ruade = undefined;
        item.energieDescription = undefined;
      }
    }

    if (!skipTransportAndEnergy && data.energyId !== undefined) {
      if (!data.energyId || data.energyId === '' || data.energyId === GlobalConstants.EMPTY_GUID) {
        item.energyId = undefined;
        item.energyName = undefined;
      } else {
        const allEnergies = [...(this.vehicleEnergies || []), ...(this.energies || [])];
        const foundEnergy = allEnergies.find((e) => e.resourceId === data.energyId);

        if (foundEnergy) {
          item.energyId = foundEnergy.resourceId;
          item.energyName = foundEnergy.displayName;
        }
      }
    }

    this.checkProjectErrors();
    this.updateData(operationId);
    this.autoSaveProject(this.currentItem);
  }

  private isInputProduct(operationId: string, item: ProductOperation): boolean {
    const op = this.findOperationById(operationId);
    return (op?.detailedOperation?.inputProducts || []).some((p) => p.operationCompositionId === item.operationCompositionId);
  }

  getInputValue(event: Event): number {
    const target = event.target as HTMLInputElement | null;
    const value = target?.value || '0';
    return +value;
  }

  editProduitOperation(operationId: string, item: ProductOperation): void {
    const dialogRef = this.dialog.open(DialogEditOperationComponent, {
      data: {
        produit: {
          transportId: item.transportId,
          transportName: item.transportName,
          energyId: item.energyId,
          energyName: item.energyName,
          energieDescription: item.energieDescription,
          quantity: item.quantity,
          isDoubleFreight: item.isDoubleFreight,
          distance: item.distance || 0,
          transportRuade: false,
          pipeProduct: item.pipeProduct,
          diameter: item.diameter,
          productionSite: item.productionSite,
          unitWeight: item.unitWeight,
          cableProduct: item.cableProduct,
          section: item.section,
          nbConductors: item.nbConductors
        },
        transports: this.inputProductsList,
        energies: this.energies
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.updateItemData(data, item, operationId);
      }
    });
  }

  editProduitMultimodalQuantity(operationId: string, item: ProductOperation): void {
    const dialogRef = this.dialog.open(DialogEditProductMultimodalQuantityComponent, {
      data: {
        quantity: item.quantity
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        item.quantity = data?.quantity;
        this.checkProjectErrors();
        this.updateData(operationId);
        this.autoSaveProject(this.currentItem);
      }
    });
  }

  editProductName(item: ProductOperation, operationId: string | undefined, operationType?: string): void {
    if (this.isLoadingProducts) return;

    this.isLoadingProducts = true;
    this.loadProductTrees(operationType).subscribe({
      next: ({ data, favoriteData, fullDatabaseTree }) => {
        this.openProductDialog(data, favoriteData, fullDatabaseTree, (selectedProducts: any[]) => {
          const newProduct = selectedProducts?.[0];
          if (newProduct) {
            this.updateProductData(item, newProduct);
            this.checkProjectErrors();
            if (operationId) this.updateData(operationId);
            this.autoSaveProject(this.currentItem);
          }
          this.isLoadingProducts = false;
        });
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.isLoadingProducts = false;
      }
    });
  }

  editProductOutputName(item: ProductOperation, operationId: string | undefined, operationType?: string): void {
    if (this.isLoadingProducts) return;

    this.isLoadingProducts = true;
    this.operationService.getProduitsSortants(operationType).subscribe({
      next: (data) => {
        this.openProductDialog(data, undefined, undefined, (selectedProducts: any[]) => {
          const newProduct = selectedProducts?.[0];
          if (newProduct) {
            this.updateProductData(item, newProduct);
            this.checkProjectErrors();
            if (operationId) this.updateData(operationId);
            this.autoSaveProject(this.currentItem);
          }
          this.isLoadingProducts = false;
        });
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.isLoadingProducts = false;
      }
    });
  }

  private loadProductTrees(operationType?: string) {
    return forkJoin({
      data: this.productFormulaService.getAll(GroupBy.family, operationType, this.currentItem.versionId),
      favoriteData: this.productFormulaService.getAll(GroupBy.favorite, operationType),
      fullDatabaseTree: this.productFormulaService.getAll(GroupBy.family, undefined, this.currentItem.versionId)
    });
  }

  private openProductDialog(data: any, favoriteData: any, fullDatabaseTree: any, callback: (result: any) => void): void {
    const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
      data: {
        title: 'dialog_edit_product_title',
        fullTree: data,
        favoriteData: favoriteData,
        fullDatabaseTree: fullDatabaseTree,
        isSingular: true,
        editMode: true
      }
    });

    dialogRef.afterClosed().subscribe(callback);
  }

  private updateProductData(item: ProductOperation, newProduct: any): void {
    item.productId = newProduct.resourceId;
    item.displayName = newProduct.displayName;
    item.productCode = newProduct.code;
    item.workUnityId = newProduct.workUnityId;
    item.workUnit = newProduct.workUnit;
    item.productIsDeleted = false;
    item.ruade = newProduct.ruade;
    item.detailedResource = newProduct.detailedResource;
    item.justified = newProduct.justified || !!(newProduct.fileName || newProduct.fCeuFileName);
  }

  deleteProduitOrEnginOperation(operationId: string, arr?: any[], index?: number): void {
    if (!arr || arr.length === 0 || index === undefined) return;

    const deletedItem = arr[index];
    const compositionId = deletedItem.operationCompositionId || deletedItem.materialCompositionId;

    if (!compositionId) {
      arr.splice(index, 1);
      this.updateData(operationId);
      this.markUnsavedIfDeferred();
      return;
    }

    this.spinnerService.show();

    let deleteObs$;
    if (deletedItem.materialCompositionId) {
      deleteObs$ = this.operationService.deleteVehicle(operationId, compositionId);
    } else if (this.isInputProduct(operationId, deletedItem)) {
      deleteObs$ = this.operationService.deleteInboundProduct(operationId, compositionId);
    } else {
      deleteObs$ = this.operationService.deleteOutboundProduct(operationId, compositionId);
    }

    deleteObs$.subscribe({
      next: () => {
        arr.splice(index, 1);
        this.updateData(operationId);
        this.spinnerService.hide();
        this.snackbarService.open(SnackbarTypes.delete);
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    });
  }

  duplicateProduct(operationId: string, arr?: ProductOperation[], index?: number, isInput: boolean = true): void {
    if (!arr || index === undefined) return;

    const src = arr[index];
    const clone: ProductOperation = {
      productId: src.productId,
      displayName: src.displayName,
      productCode: src.productCode,
      energyId: src.energyId,
      transportId: src.transportId,
      distance: src.distance,
      quantity: src.quantity,
      workUnit: src.workUnit,
      workUnityId: src.workUnityId,
      pipeProduct: src.pipeProduct,
      cableProduct: src.cableProduct,
      ruade: src.ruade,
      isMultimodal: src.isMultimodal,
      justified: src.justified,
      detailedResource: src.detailedResource
    };

    this.spinnerService.show();
    const addObs$ = isInput ? this.operationService.addInboundProduct(operationId, clone) : this.operationService.addOutboundProduct(operationId, clone);
    addObs$.subscribe({
      next: (added: ProductOperation) => {
        arr.splice(index + 1, 0, added);
        this.updateData(operationId);
        this.markUnsavedIfDeferred();
        this.spinnerService.hide();
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    });
  }

  addInputProduit(operationId: string, products: ProductOperation[], operationType?: string): void {
    if (this.isLoadingProducts) return;

    this.isLoadingProducts = true;
    this.loadProductTrees(operationType).subscribe({
      next: ({ data, favoriteData, fullDatabaseTree }) => {
        this.openProductSelectionDialog(data, favoriteData, fullDatabaseTree, (dataResult) => {
          if (dataResult && dataResult.length > 0) {
            this.spinnerService.show();
            const productsToAdd = this.buildProductsFromSelection(dataResult);
            const addCalls = productsToAdd.map((p: ProductOperation) => this.operationService.addInboundProduct(operationId, p));
            forkJoin(addCalls).subscribe({
              next: (addedProducts: ProductOperation[]) => {
                this.sortByRank(addedProducts).forEach((p) => products.push(p));
                this.updateData(operationId);
                this.markUnsavedIfDeferred();
                this.spinnerService.hide();
              },
              error: () => {
                this.snackbarService.open(SnackbarTypes.error);
                this.spinnerService.hide();
              }
            });
          }
          this.isLoadingProducts = false;
        });
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.isLoadingProducts = false;
      }
    });
  }

  addOutputProduit(operationId: string, products: ProductOperation[], operationType?: string): void {
    if (this.isLoadingProducts) return;

    this.isLoadingProducts = true;
    this.operationService.getProduitsSortants(operationType).subscribe({
      next: (data) => {
        this.openProductCompositionDialog(data, (dataResult) => {
          if (dataResult && dataResult.length > 0) {
            this.spinnerService.show();
            const productsToAdd = this.buildProductsFromSelection(dataResult);
            const addCalls = productsToAdd.map((p: ProductOperation) => this.operationService.addOutboundProduct(operationId, p));
            forkJoin(addCalls).subscribe({
              next: (addedProducts: ProductOperation[]) => {
                this.sortByRank(addedProducts).forEach((p) => products.push(p));
                this.updateData(operationId);
                this.markUnsavedIfDeferred();
                this.spinnerService.hide();
              },
              error: () => {
                this.snackbarService.open(SnackbarTypes.error);
                this.spinnerService.hide();
              }
            });
          }
          this.isLoadingProducts = false;
        });
      },
      error: () => {
        this.snackbarService.open(SnackbarTypes.error);
        this.isLoadingProducts = false;
      }
    });
  }

  private openProductSelectionDialog(data: any, favoriteData: any, fullDatabaseTree: any, callback: (result: any) => void): void {
    const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
      data: {
        title: 'dialog_add_product_title',
        fullTree: data,
        favoriteData: favoriteData,
        fullDatabaseTree: fullDatabaseTree,
        filterEmptyParents: true
      }
    });

    dialogRef.afterClosed().subscribe(callback);
  }

  private openProductCompositionDialog(data: any, callback: (result: any) => void): void {
    const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
      data: {
        fullTree: data
      }
    });

    dialogRef.afterClosed().subscribe(callback);
  }

  private buildProductsFromSelection(dataResult: any[]): ProductOperation[] {
    return dataResult.map((el: any) => ({
      productId: el?.resourceId,
      displayName: el?.displayName,
      productCode: el?.code,
      energyId: el?.energyId,
      transportId: el?.transportId,
      distance: 0,
      quantity: 0,
      workUnit: el?.workUnit,
      workUnityId: el?.workUnityId,
      pipeProduct: el?.pipeProduct,
      cableProduct: el?.cableProduct,
      ruade: el?.ruade,
      justified: el?.justified || !!(el?.fileName || el?.fCeuFileName),
      detailedResource: el?.detailedResource
    }));
  }

  editOperationEngin(item: Vehicle, operationId: string, operationType?: string): void {
    forkJoin({
      data: this.enginService.getAll(undefined, operationType),
      favoriteData: this.enginService.getAll(GroupBy.favoriteDialog, operationType),
      fullDatabaseTree: this.enginService.getAll()
    }).subscribe(({ data, favoriteData, fullDatabaseTree }) => {
      const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
        data: {
          title: 'dialog_edit_engin_title',
          fullTree: data,
          favoriteData: favoriteData,
          fullDatabaseTree: fullDatabaseTree,
          editMode: true,
          isSingular: true
        }
      });

      dialogRef.afterClosed().subscribe((dataResult) => {
        if (dataResult?.length > 0) {
          Object.assign(item, dataResult[0]);
          item.transportIsDeleted = false;
          if (dataResult[0].resourceId) {
            item.vehicleId = dataResult[0].resourceId;
          }
          item.isAtelier = !!dataResult[0].detailedResource;
          const allEnergies = [...(this.vehicleEnergies || []), ...(this.energies || [])];
          item.energyName = this.isBlankId(item.energyId) ? undefined : allEnergies.find((e) => e.resourceId === item.energyId)?.displayName;
          this.updateData(operationId);
          this.autoSaveProject(this.currentItem);
        }
      });
    });
  }

  addEnginOperation(operation: Operation): void {
    forkJoin({
      data: this.enginService.getAll(undefined, operation.type),
      favoriteData: this.enginService.getAll(GroupBy.favoriteDialog, operation.type),
      fullDatabaseTree: this.enginService.getAll()
    }).subscribe(({ data, favoriteData, fullDatabaseTree }) => {
      if (operation.type == 'MaritimeWork') {
        const dredgeLabel = this.translateService.instant('engin_category_maritime_dredge');
        (data as any)[dredgeLabel] = {
          0: {
            code: 'DRA',
            detailedResource: false,
            displayName: dredgeLabel,
            index: 0,
            resourceType: ResourceType.Dredge,
            resourceId: GlobalConstants.EMPTY_GUID
          }
        };
      }
      if (operation.type == 'UndergroundWork') {
        const tunnelierLabel = this.translateService.instant('common_field_tunneling');
        (data as any)[tunnelierLabel] = {
          0: {
            code: 'TUN',
            detailedResource: false,
            displayName: tunnelierLabel,
            index: 0,
            resourceType: ResourceType.Tunnelier,
            resourceId: GlobalConstants.EMPTY_GUID
          }
        };
      }

      const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
        data: {
          title: 'dialog_add_engin_title',
          fullTree: data,
          favoriteData: favoriteData,
          fullDatabaseTree: fullDatabaseTree
        }
      });

      dialogRef.afterClosed().subscribe((dataResult) => {
        if (!dataResult || dataResult.length === 0) return;

        if (!operation.detailedOperation) {
          operation.detailedOperation = {};
        }
        if (!operation.detailedOperation.vehicles) {
          operation.detailedOperation.vehicles = [];
        }

        const engins = operation.detailedOperation.vehicles;
        const vehiclesToAdd: Vehicle[] = [];
        let hasLocalEquipment = false;

        dataResult.forEach((el: any) => {
          if (el.resourceType == ResourceType.Dredge) {
            if (!operation.detailedOperation!.dredging) {
              operation.detailedOperation!.dredging = [];
            }
            operation.detailedOperation!.dredging.push({
              displayName: el.displayName
            });
            hasLocalEquipment = true;
          } else if (el.resourceType == ResourceType.Tunnelier) {
            if (!operation.detailedOperation!.tunneling) {
              operation.detailedOperation!.tunneling = [];
            }
            operation.detailedOperation!.tunneling.push({
              displayName: el.displayName
            });
            hasLocalEquipment = true;
          } else {
            if (el?.energyId && el?.energyId !== GlobalConstants.EMPTY_GUID) {
              const allEnergies = [...(this.vehicleEnergies || []), ...(this.energies || [])];
              el.energyName = allEnergies.find((e: Energie) => e.resourceId == el?.energyId)?.displayName ?? this.translateService.instant('energy_deleted_label');
            }
            vehiclesToAdd.push({
              displayName: el?.displayName,
              vehicleId: el?.resourceId,
              isAtelier: el?.detailedResource,
              quantity: 0,
              rank: engins.length,
              energyId: el?.energyId,
              energyName: el?.energyName
            });
          }
        });

        if (hasLocalEquipment) this.markUnsavedIfDeferred();

        if (vehiclesToAdd.length > 0) {
          this.spinnerService.show();
          const addCalls = vehiclesToAdd.map((v) => this.operationService.addVehicle(operation.id!, v));
          forkJoin(addCalls).subscribe({
            next: (addedVehicles: Vehicle[]) => {
              this.sortByRank(addedVehicles.map((v, i) => ({ ...vehiclesToAdd[i], ...v }))).forEach((v) => {
                v.energyName = this.isBlankId(v.energyId) ? undefined : this.vehicleEnergies.find((e) => e.resourceId === v.energyId)?.displayName;
                if (v.atelierEngins) {
                  for (let ae of v.atelierEngins) {
                    ae.energyName = this.isBlankId(ae.energyId) ? undefined : this.vehicleEnergies.find((e) => e.resourceId === ae.energyId)?.displayName;
                  }
                }
                engins.push(v);
              });

              this.markUnsavedIfDeferred();
              this.updateData(operation.id!);
              this.spinnerService.hide();
            },
            error: () => {
              this.snackbarService.open(SnackbarTypes.error);
              this.spinnerService.hide();
            }
          });
        } else {
          this.updateData(operation.id!);
        }
      });
    });
  }

  updateEnginQuantity(item: Vehicle, quantity: number, operationId: string): void {
    if (this.displayAsDay) {
      item.quantity = quantity;
    } else {
      item.quantity = this.getDayFromHours(quantity);
    }
    this.updateData(operationId);
    this.autoSaveProject(this.currentItem);

    if (!item.materialCompositionId) return;
    if (this.shouldDeferPersist()) return;

    this.operationService.updateVehicle(operationId, item.materialCompositionId, item).subscribe({
      error: () => this.snackbarService.open(SnackbarTypes.error)
    });
  }

  editEnginOperation(item: any, operationId: string, isTunneling = false, isDredging = false): void {
    if (isDredging) {
      const dialogRef = this.dialog.open(DialogDredgingEquipmentComponent, {
        maxHeight: '80vh',
        data: item,
        panelClass: 'dredging-equipment-dialog'
      });

      dialogRef.afterClosed().subscribe((result: DialogDredgingEquipmentComponentResponseDataModel) => {
        if (result) {
          Object.assign(item, result.dredge);
          if (result.selectedFile != null) {
            this.uploadLocalEquipmentProof(operationId, item, 'dredging', result.selectedFile);
          } else {
            this.persistOperationFileChange(operationId);
            this.updateData(operationId);
            this.autoSaveProject(this.currentItem);
          }
        }
      });
      return;
    }

    if (isTunneling) {
      const dialogRef = this.dialog.open(DialogTunnelingEquipmentComponent, {
        maxHeight: '90vh',
        data: { tunneler: item, versionId: this.form.value.versionId },
        panelClass: 'tunneling-equipment-dialog'
      });

      dialogRef.afterClosed().subscribe((result: DialogTunnelingEquipmentComponentResponseDataModel) => {
        if (result) {
          Object.assign(item, result.tunneler);
          item.energyName = this.resolveTunnelerEnergyName(result.tunneler.selectedFuel);
          if (result.selectedFile != null) {
            this.uploadLocalEquipmentProof(operationId, item, 'tunneling', result.selectedFile);
          } else {
            this.persistOperationFileChange(operationId);
            this.updateData(operationId);
            this.autoSaveProject(this.currentItem);
          }
        }
      });
      return;
    }

    const quantityToSendAsData = this.displayAsDay ? item.quantity : this.getHoursFromDay(item.quantity);

    const dialogRef = this.dialog.open(DialogEditEnginComponent, {
      data: {
        quantity: quantityToSendAsData,
        displayName: item.displayName,
        displayAsDay: this.displayAsDay
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        if (this.displayAsDay) {
          item.quantity = data?.quantity;
        } else {
          item.quantity = this.getDayFromHours(data?.quantity);
        }
        this.updateData(operationId);
        this.autoSaveProject(this.currentItem);
      }
    });
  }

  getHoursFromDay(hours?: number): number {
    if (!this.hoursForDay || !hours) {
      return 0;
    }
    return Math.round(hours * this.hoursForDay);
  }

  getDayFromHours(day?: number): number {
    if (this.hoursForDay && day) {
      return day / this.hoursForDay;
    }
    return 0;
  }

  trackByIndexFn(index: any): any {
    return index;
  }

  toggleProductPanels(itemId: any): void {
    const item = this.panelStates.find((i) => i.id === itemId);

    if (!item) {
      this.panelStates.push({ id: itemId, expanded: true });
    } else {
      item.expanded = !item.expanded;
    }
  }

  getPanelState(itemId: any): boolean | undefined {
    return this.panelStates.find((i) => i.id === itemId)?.expanded;
  }

  checkIncompleteProducts(): void {
    this.incompleteProductsList = [];
    this.showIncompleteProductsWarning = false;

    if (!this.currentItem?.solutions || this.selected.value < 0) return;

    const currentSolution = this.currentItem.solutions[this.selected.value];
    if (!currentSolution?.operations) return;

    currentSolution.operations.forEach((operation) => {
      if (!operation.detailedOperation) return;

      const inputProducts = operation.detailedOperation.inputProducts || [];
      const outputProducts = operation.detailedOperation.outputProducts || [];

      [...inputProducts, ...outputProducts].forEach((product) => {
        const isIncomplete = this.isProductIncomplete(product);
        if (isIncomplete) {
          this.incompleteProductsList.push(`${operation.displayName} - ${product.displayName} [${product.productCode}]`);
        }
      });
    });

    this.showIncompleteProductsWarning = this.incompleteProductsList.length > 0;
  }

  private isProductIncomplete(product: ProductOperation): boolean {
    if (!product) return false;

    const hasTransport = product.transportId && product.transportId !== '0' && product.transportId !== '';
    const hasEnergy = (product.energyId && product.energyId !== '0' && product.energyId !== '') || (product.energieDescription && product.energieDescription !== '');
    const hasDistance = product.distance !== undefined && product.distance !== null && product.distance > 0;
    const hasQuantity = product.quantity !== undefined && product.quantity !== null && product.quantity > 0;

    return !hasTransport || !hasEnergy || !hasDistance || !hasQuantity;
  }

  hideIncompleteProductsWarning(): void {
    this.showIncompleteProductsWarning = false;
  }

  private uploadLocalEquipmentProof(operationId: string, item: DredgingProjectUsageResourceModel | TunnelingProjectUsageResourceModel, kind: EquipmentProofKind, file: File): void {
    const operation = this.findOperationById(operationId);
    if (!operation?.detailedOperation) return;

    const upload = (id: string) => {
      const formData = new FormData();
      formData.append('file', file);
      const upload$ = kind === 'dredging' ? this.uploadService.uploadDredgeProof(id, formData) : this.uploadService.uploadTunnelingProof(id, formData);
      upload$.subscribe({
        next: (event) => {
          if (event.type !== HttpEventType.Response) return;
          this.spinnerService.hide();
          this.snackbarService.open(SnackbarTypes.update);
          this.updateData(operationId);
          this.autoSaveProject(this.currentItem, true);
        },
        error: () => {
          this.spinnerService.hide();
          this.updateData(operationId);
          this.autoSaveProject(this.currentItem, true);
          this.snackbarService.open(SnackbarTypes.error);
        }
      });
    };

    if (item.id) {
      this.spinnerService.show();
      upload(item.id);
      return;
    }

    if (this.shouldDeferPersist()) {
      this.onEquipmentProofPending.emit({ item, kind, file });
      this.updateData(operationId);
      return;
    }

    this.spinnerService.show();
    this.operationService.update(operationId, operation).subscribe({
      next: (saved) => {
        if (saved) assignCreatedEquipmentIds(operation, saved);
        if (item.id) {
          upload(item.id);
        } else {
          this.spinnerService.hide();
          this.snackbarService.open(SnackbarTypes.error);
        }
      },
      error: () => {
        this.spinnerService.hide();
        this.snackbarService.open(SnackbarTypes.error);
      }
    });
  }

  private persistOperationFileChange(operationId: string): void {
    const operation = this.findOperationById(operationId);
    if (!operation) return;
    if (this.shouldDeferPersist()) return;
    this.operationService.update(operationId, operation).subscribe({
      error: () => this.snackbarService.open(SnackbarTypes.error)
    });
  }

  autoSaveProject(currentItem: Project, silent = false): void {
    this.saveSolutionFormData();
    if (this.shouldDeferPersist()) return;

    this.onSaveProject.emit({ item: currentItem, silent });
  }

  markUnsavedIfDeferred(): void {
    if (this.autoSaveEnabled) return;

    this.autoSaveService.markUnsaved();
  }

  private get hasDeferredChanges(): boolean {
    return !this.autoSaveEnabled && this.autoSaveService.hasUnsavedChanges;
  }

  private shouldDeferPersist(): boolean {
    this.markUnsavedIfDeferred();
    return !this.autoSaveEnabled;
  }

  private saveSolutionFormData(): void {
    if (!this.currentItem?.solutions || this.selected.value < 0 || !this.solutionForm) return;

    const currentSolution = this.currentItem.solutions[this.selected.value];
    if (!currentSolution) return;

    const formValue = this.solutionForm.value;
    this.currentItem.callForTendersDisplayName = formValue.callForTendersDisplayName;
    currentSolution.description = formValue.description;
    currentSolution.waterIndicator = formValue.waterIndicator;
    currentSolution.biodiversityIndicator = formValue.biodiversityIndicator;
  }

  handleNextStep(): void {
    this.saveSolutionFormData();
    this.onNextStep.emit();
  }

  handlePreviousStep(): void {
    this.saveSolutionFormData();
    this.onPreviousStep.emit();
  }
}
